/**
 * Seed data & local storage manager for UPSC Admin Panel
 * Provides immediate end-to-end functionality for Questions, Reports, Tests, Users, and Audit Logs
 */

const STORAGE_KEY_QUESTIONS = "upsc_admin_question_bank_v1";
const STORAGE_KEY_REPORTS = "upsc_admin_question_reports_v1";
const STORAGE_KEY_TESTS = "upsc_admin_tests_v1";
const STORAGE_KEY_AUDIT_LOGS = "upsc_admin_audit_logs_v1";

export const INITIAL_QUESTIONS = [
  {
    id: "Q-1001",
    subject: "Polity",
    topic: "Fundamental Rights",
    subtopic: "Article 21",
    question: "Which one of the following statements about the Right to Privacy in India is correct?",
    options: {
      A: "It is a statutory right enacted under the Information Technology Act.",
      B: "It is an intrinsic part of the Right to Life and Personal Liberty under Article 21.",
      C: "It is an absolute right that cannot be curtailed under any circumstance.",
      D: "It is recognized only in civil disputes between private individuals."
    },
    correct_answer: "B",
    explanation: "In Justice K.S. Puttaswamy (Retd.) v. Union of India (2017), a nine-judge Constitution Bench held that the Right to Privacy is protected as an intrinsic part of the right to life and personal liberty under Article 21.",
    difficulty: "Medium",
    pyq_year: "2018",
    marks: 2,
    negative_marks: 0.66,
    version: 1,
    history: [
      {
        version: 1,
        timestamp: "2024-01-15T10:00:00Z",
        editedBy: "academics@edukemy.com",
        changes: "Initial question creation"
      }
    ],
    status: "Active",
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z"
  },
  {
    id: "Q-1002",
    subject: "Polity",
    topic: "Preamble & Constitution",
    subtopic: "Basic Structure Doctrine",
    question: "The 'Basic Structure Doctrine' was propounded by the Supreme Court of India in which landmark case?",
    options: {
      A: "Golaknath Case (1967)",
      B: "Kesavananda Bharati Case (1973)",
      C: "Minerva Mills Case (1980)",
      D: "Maneka Gandhi Case (1978)"
    },
    correct_answer: "B",
    explanation: "The doctrine of basic structure was laid down by the Supreme Court in the landmark Kesavananda Bharati v. State of Kerala case (1973).",
    difficulty: "Easy",
    pyq_year: "2019",
    marks: 2,
    negative_marks: 0.66,
    version: 1,
    history: [
      {
        version: 1,
        timestamp: "2024-01-16T11:20:00Z",
        editedBy: "academics@edukemy.com",
        changes: "Initial question creation"
      }
    ],
    status: "Active",
    created_at: "2024-01-16T11:20:00Z",
    updated_at: "2024-01-16T11:20:00Z"
  },
  {
    id: "Q-1003",
    subject: "History",
    topic: "Modern Indian History",
    subtopic: "Indian National Congress",
    question: "Who was the Viceroy of India when the Indian National Congress was founded in 1885?",
    options: {
      A: "Lord Ripon",
      B: "Lord Dufferin",
      C: "Lord Curzon",
      D: "Lord Lytton"
    },
    correct_answer: "B",
    explanation: "Lord Dufferin was the Viceroy of India when the Indian National Congress was founded by Allan Octavian Hume in December 1885.",
    difficulty: "Easy",
    pyq_year: "2015",
    marks: 2,
    negative_marks: 0.66,
    version: 1,
    history: [
      {
        version: 1,
        timestamp: "2024-01-17T09:15:00Z",
        editedBy: "academics@edukemy.com",
        changes: "Initial question creation"
      }
    ],
    status: "Active",
    created_at: "2024-01-17T09:15:00Z",
    updated_at: "2024-01-17T09:15:00Z"
  },
  {
    id: "Q-1004",
    subject: "Economy",
    topic: "Monetary Policy & Banking",
    subtopic: "Monetary Policy Committee",
    question: "With reference to the Monetary Policy Committee (MPC), consider the following statements:\n1. It decides the RBI's benchmark repo rate.\n2. It is a 12-member committee including the Governor of RBI.\n3. The Governor of RBI has a casting vote in case of a tie.\nWhich of the statements given above is/are correct?",
    options: {
      A: "1 only",
      B: "1 and 2 only",
      C: "1 and 3 only",
      D: "1, 2 and 3"
    },
    correct_answer: "C",
    explanation: "Statement 1 is correct (decides repo rate). Statement 2 is incorrect (MPC is a 6-member committee, not 12). Statement 3 is correct (Governor of RBI has a second or casting vote).",
    difficulty: "Medium",
    pyq_year: "2017",
    marks: 2,
    negative_marks: 0.66,
    version: 1,
    history: [
      {
        version: 1,
        timestamp: "2024-01-18T14:40:00Z",
        editedBy: "academics@edukemy.com",
        changes: "Initial question creation"
      }
    ],
    status: "Active",
    created_at: "2024-01-18T14:40:00Z",
    updated_at: "2024-01-18T14:40:00Z"
  },
  {
    id: "Q-1005",
    subject: "Environment",
    topic: "Biodiversity & Conservation",
    subtopic: "National Parks",
    question: "Which of the following National Parks is unique in being a swamp with floating vegetation that supports a rich biodiversity?",
    options: {
      A: "Bhitarkanika National Park",
      B: "Keibul Lamjao National Park",
      C: "Keoladeo Ghana National Park",
      D: "Sultanpur National Park"
    },
    correct_answer: "B",
    explanation: "Keibul Lamjao National Park in Manipur is the world's only floating national park, located on Loktak Lake, characterized by floating decomposed plant material called 'Phumdis'. It is the last natural habitat of the Eld's deer (Sangai).",
    difficulty: "Medium",
    pyq_year: "2015",
    marks: 2,
    negative_marks: 0.66,
    version: 1,
    history: [
      {
        version: 1,
        timestamp: "2024-01-19T16:00:00Z",
        editedBy: "academics@edukemy.com",
        changes: "Initial question creation"
      }
    ],
    status: "Active",
    created_at: "2024-01-19T16:00:00Z",
    updated_at: "2024-01-19T16:00:00Z"
  },
  {
    id: "Q-1006",
    subject: "Geography",
    topic: "Physical Geography",
    subtopic: "Indian River System",
    question: "Which one of the following pairs of rivers and their tributaries is correctly matched?",
    options: {
      A: "Godavari - Manjira",
      B: "Krishna - Indravati",
      C: "Cauvery - Koyna",
      D: "Mahanadi - Tungabhadra"
    },
    correct_answer: "A",
    explanation: "Manjira is a right-bank tributary of the river Godavari. Indravati is a tributary of Godavari (not Krishna). Koyna and Tungabhadra are tributaries of Krishna.",
    difficulty: "Hard",
    pyq_year: "2020",
    marks: 2,
    negative_marks: 0.66,
    version: 1,
    history: [
      {
        version: 1,
        timestamp: "2024-01-20T12:00:00Z",
        editedBy: "academics@edukemy.com",
        changes: "Initial question creation"
      }
    ],
    status: "Active",
    created_at: "2024-01-20T12:00:00Z",
    updated_at: "2024-01-20T12:00:00Z"
  }
];

export const INITIAL_QUESTION_REPORTS = [
  {
    id: "REP-201",
    question_id: "Q-1004",
    reporter_user_id: "usr_8821",
    reporter_email: "aspirant.vikram@gmail.com",
    reason: "Incorrect option in explanation or question ambiguity",
    user_comment: "The explanation mentions 6 members for MPC, please double check if Governor has casting vote in all situations.",
    status: "Pending", // Pending, Resolved, Rejected
    resolution_notes: "",
    resolved_by: null,
    created_at: "2024-02-10T14:30:00Z",
    updated_at: "2024-02-10T14:30:00Z"
  },
  {
    id: "REP-202",
    question_id: "Q-1006",
    reporter_user_id: "usr_3342",
    reporter_email: "priya.sharma@outlook.com",
    reason: "Typographical Error",
    user_comment: "Small typo in option C spelling of Cauvery/Koyna.",
    status: "Resolved",
    resolution_notes: "Corrected option text in question bank v2.",
    resolved_by: "academics@edukemy.com",
    created_at: "2024-02-05T09:20:00Z",
    updated_at: "2024-02-06T11:00:00Z"
  }
];

export const INITIAL_TESTS = [
  {
    id: "TEST-501",
    title: "UPSC Prelims 2024 - GS Mini Mock 1",
    code: "UPSC-PRE-2024-MM01",
    description: "Comprehensive mini mock test covering Indian Polity, Modern History, and Environment.",
    duration_minutes: 60,
    total_marks: 50,
    negative_marking: 0.66,
    sections: [
      {
        id: "SEC-1",
        title: "Indian Polity & Constitution",
        description: "Articles, Rights, and Supreme Court Doctrines",
        question_ids: ["Q-1001", "Q-1002"]
      },
      {
        id: "SEC-2",
        title: "History, Economy & Environment",
        description: "Modern Indian History, Monetary Policy & Ecology",
        question_ids: ["Q-1003", "Q-1004", "Q-1005", "Q-1006"]
      }
    ],
    status: "Published", // Draft, Published, Inactive
    is_active: true,
    availability_start: "2024-01-01T00:00:00Z",
    availability_end: "2025-12-31T23:59:59Z",
    attempt_limit: 3,
    plan_tier: "All", // All, Free, Premium
    created_by: "academics@edukemy.com",
    created_at: "2024-01-25T10:00:00Z",
    updated_at: "2024-01-26T15:30:00Z"
  },
  {
    id: "TEST-502",
    title: "Polity Sectional Test - Fundamental Rights & Preamble",
    code: "SEC-POL-01",
    description: "Targeted evaluation for Prelims Polity chapters.",
    duration_minutes: 30,
    total_marks: 20,
    negative_marking: 0.66,
    sections: [
      {
        id: "SEC-POL",
        title: "Polity Core",
        description: "Preamble and Articles 12-35",
        question_ids: ["Q-1001", "Q-1002"]
      }
    ],
    status: "Draft",
    is_active: false,
    availability_start: "2024-03-01T00:00:00Z",
    availability_end: "2025-12-31T23:59:59Z",
    attempt_limit: 1,
    plan_tier: "Premium",
    created_by: "academics@edukemy.com",
    created_at: "2024-02-01T14:00:00Z",
    updated_at: "2024-02-01T14:00:00Z"
  }
];

export const INITIAL_AUDIT_LOGS = [
  {
    id: "AUD-9001",
    admin_user: "superadmin@edukemy.com",
    action: "Test Published",
    module: "Tests",
    entity_type: "Test",
    entity_id: "TEST-501",
    timestamp: "2024-01-26T15:30:00Z",
    before_value: { status: "Draft", is_active: false },
    after_value: { status: "Published", is_active: true }
  },
  {
    id: "AUD-9002",
    admin_user: "academics@edukemy.com",
    action: "Question Report Resolved",
    module: "Questions",
    entity_type: "QuestionReport",
    entity_id: "REP-202",
    timestamp: "2024-02-06T11:00:00Z",
    before_value: { status: "Pending" },
    after_value: { status: "Resolved", resolution_notes: "Corrected option text in question bank v2." }
  }
];

// Local storage helpers
export const getStoredQuestions = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_QUESTIONS);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY_QUESTIONS, JSON.stringify(INITIAL_QUESTIONS));
      return INITIAL_QUESTIONS;
    }
    return JSON.parse(raw);
  } catch (e) {
    return INITIAL_QUESTIONS;
  }
};

export const saveStoredQuestions = (questions) => {
  localStorage.setItem(STORAGE_KEY_QUESTIONS, JSON.stringify(questions));
};

export const getStoredReports = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_REPORTS);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY_REPORTS, JSON.stringify(INITIAL_QUESTION_REPORTS));
      return INITIAL_QUESTION_REPORTS;
    }
    return JSON.parse(raw);
  } catch (e) {
    return INITIAL_QUESTION_REPORTS;
  }
};

export const saveStoredReports = (reports) => {
  localStorage.setItem(STORAGE_KEY_REPORTS, JSON.stringify(reports));
};

export const getStoredTests = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TESTS);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY_TESTS, JSON.stringify(INITIAL_TESTS));
      return INITIAL_TESTS;
    }
    return JSON.parse(raw);
  } catch (e) {
    return INITIAL_TESTS;
  }
};

export const saveStoredTests = (tests) => {
  localStorage.setItem(STORAGE_KEY_TESTS, JSON.stringify(tests));
};

export const getStoredAuditLogs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_AUDIT_LOGS);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY_AUDIT_LOGS, JSON.stringify(INITIAL_AUDIT_LOGS));
      return INITIAL_AUDIT_LOGS;
    }
    return JSON.parse(raw);
  } catch (e) {
    return INITIAL_AUDIT_LOGS;
  }
};

export const saveStoredAuditLogs = (logs) => {
  localStorage.setItem(STORAGE_KEY_AUDIT_LOGS, JSON.stringify(logs));
};
